import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { showLoading, hideLoading } from "../../store/slices/loading/loadingSlice";
import RouteAccessService from "../../services/access/RouteAccessService";
import {
  setAccessResult,
  resetAccessState,
  setNavigating,
  setHoldTransition,
  setCurrentRoute,
} from "../../store/slices/access/routeAccessSlice";
import {
  showTopProgress,
  setTopProgress,
  hideTopProgress,
} from "../../store/slices/ui/topProgressSlice";

/**
 * 🎯 Hook: `useRouteAccess`
 * ------------------------------------------------------------
 * Gestiona la **validación de acceso a rutas protegidas** y el **control de transición**
 * entre páginas del sistema.
 *
 * Coordina:
 * - Validación de permisos de acceso con el backend.
 * - Actualización del estado global (`routeAccessSlice`).
 * - Control visual de loaders y barras de progreso.
 * - Manejo de pausas de navegación (`holdTransition`).
 * - Sincronización de la ruta visible (`currentRoute`) con la validación real.
 *
 * 🔗 Usado principalmente por:
 * - `ProtectedRouteV2` → para bloquear, pausar o autorizar la navegación.
 * - Flujos de confirmación (ej. salir de un formulario sin guardar).
 */
export const useRouteAccess = () => {
  // =====================================================
  // ⚙️ CONFIGURACIÓN Y ESTADO GLOBAL
  // =====================================================
  const dispatch: AppDispatch = useDispatch();

  /**
   * 📊 Estado global de control de acceso.
   * Contiene información sobre la validación, ruta visible y transiciones activas.
   */
  const {
    allowed,
    lastCheckedRoute,
    currentRoute,
    isNavigating,
    holdTransition,
  } = useSelector((state: RootState) => state.routeAccess);

  // =====================================================
  // 🌐 FUNCIÓN PRINCIPAL: VALIDAR ACCESO
  // =====================================================

  /**
   * 🌐 `checkAccess(route: string)`
   * ------------------------------------------------------
   * Verifica si el usuario tiene permiso para acceder a una ruta protegida.
   *
   * Flujo:
   * 1️⃣ Marca inicio de validación (`isNavigating = true`).
   * 2️⃣ Reinicia estado previo (`resetAccessState`).
   * 3️⃣ Muestra barra de progreso + loader visual.
   * 4️⃣ Llama al backend → `RouteAccessService.checkAccess(route)`.
   * 5️⃣ Actualiza estado global con resultado (`setAccessResult`).
   * 6️⃣ Si el acceso es permitido, sincroniza `currentRoute`.
   * 7️⃣ Oculta loader y completa la barra de progreso.
   *
   * @param route Ruta a validar (ya normalizada, sin query params ni hash).
   * @returns Promise<boolean> → `true` si tiene acceso, `false` si no.
   *
   * @example
   * ```ts
   * const { checkAccess } = useRouteAccess();
   * const canEnter = await checkAccess("/dashboard/reports");
   * if (!canEnter) navigate("/unauthorized");
   * ```
   */
  const checkAccess = useCallback(async (route: string): Promise<boolean> => {
    let progress = 0;
    let interval: NodeJS.Timeout | null = null;

    try {
      // 🚀 Iniciar validación
      // Nota: NO se despacha resetAccessState() aquí — hacerlo pone
      // lastCheckedRoute en null mientras la validación está en curso,
      // rompiendo el guard "lastCheckedRoute !== cleanPath" que usa
      // ProtectedRouteV2 para no volver a invocar checkAccess, lo cual
      // reentraba en un loop ("Maximum update depth exceeded"). El
      // setAccessResult del final ya deja el slice completamente
      // consistente (allowed, lastCheckedRoute, isNavigating, holdTransition).
      dispatch(setNavigating(true));

      // 🎬 Mostrar loader y barra superior
      dispatch(showTopProgress("top"));
      dispatch(setTopProgress(0));
      dispatch(showLoading());

      // 🔁 Simulación visual de avance (hasta 95%)
      interval = setInterval(() => {
        progress = Math.min(progress + Math.random() * 8 + 4, 95);
        dispatch(setTopProgress(progress));
      }, 120);

      // 🌐 Llamada real al backend
      const response = await RouteAccessService.checkAccess(route);
      const { statusCode, data } = response;

      // 🧩 Actualizar resultado global
      if (statusCode === 200 || statusCode === 403) {
        dispatch(setAccessResult({ route, result: data }));

        // ✅ Si se permitió acceso → actualizar ruta visible actual
        if (data.allowed) {
          dispatch(setCurrentRoute(route));
        }
      }

      return data.allowed;
    } catch (error) {
      // ⚠️ En caso de error → negar acceso de forma segura
      dispatch(setAccessResult({ route, result: { allowed: false } }));
      return false;
    } finally {
      // 🧹 Limpiar intervalos
      if (interval) clearInterval(interval);

      // 🟩 Completar progresivamente hasta 100%
      while (progress < 100) {
        progress = Math.min(progress + 1.5, 100);
        dispatch(setTopProgress(progress));
        await new Promise((r) => setTimeout(r, 8));
      }

      // Pausa corta antes de ocultar loader
      await new Promise((r) => setTimeout(r, 300));

      dispatch(hideLoading());
      dispatch(hideTopProgress());
      dispatch(setNavigating(false));
    }
  }, [dispatch]);

  // =====================================================
  // 🔧 MÉTODOS AUXILIARES DE CONTROL DE ESTADO
  // =====================================================

  /**
   * 🔄 Reinicia completamente el estado global de validación de rutas.
   * Se usa normalmente al cerrar sesión o limpiar el contexto de usuario.
   */
  const reset = () => dispatch(resetAccessState());

  /**
   * ⏸️ Pausa la transición entre rutas protegidas.
   *
   * Ideal cuando se requiere confirmar una acción antes de abandonar la página,
   * como formularios o flujos con datos pendientes.
   */
  const hold = () => dispatch(setHoldTransition(true));

  /**
   * ▶️ Libera la transición pausada y permite continuar la navegación.
   *
   * Se usa después de que el usuario confirma o completa la acción pendiente.
   */
  const release = () => dispatch(setHoldTransition(false));

  /**
   * 🧭 Establece manualmente la ruta actual visible.
   * Útil para sincronizar el estado visual después de flujos controlados.
   */
  const setCurrent = (route: string) => dispatch(setCurrentRoute(route));

  // =====================================================
  // 📤 RETORNO DEL HOOK
  // =====================================================

  return {
    /** Resultado de acceso actual (true, false o null). */
    allowed,

    /** Última ruta validada por el sistema. */
    lastCheckedRoute,

    /** Ruta actualmente visible (efectiva) para el usuario. */
    currentRoute,

    /** Indica si hay una validación de acceso en curso. */
    isNavigating,

    /** Indica si la transición entre rutas está pausada globalmente. */
    holdTransition,

    /** Verifica el acceso a una ruta específica. */
    checkAccess,

    /** Reinicia el estado global de validación. */
    reset,

    /** Pausa temporalmente la navegación. */
    hold,

    /** Libera la navegación pausada. */
    release,

    /** Fuerza la sincronización de la ruta visible actual. */
    setCurrent,
  };
};

export default useRouteAccess;
